import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LocalHospitalOutlinedIcon from "@mui/icons-material/LocalHospitalOutlined";
import type { MyFacilityListRow } from "types/myFacilityListRow";
import {
  getNetworkSummary,
  getNetworkSummaryForNetwork,
  getNetworkRiskFlags,
  getNetworkRiskFlagsForNetwork,
  getNetworkRatings,
  getNetworkRatingsForNetwork,
  getNetworkSurveyDrivers,
  getNetworkSurveyDriversForNetwork,
  getNetworkStaffing,
  getNetworkStaffingForNetwork,
  getNetworkQualityMeasures,
  getNetworkQualityMeasuresForNetwork,
  getNetworkVbp,
  getNetworkVbpForNetwork,
  getNetworkExecutiveSummary,
  getNetworkExecutiveSummaryForNetwork,
  findNetworkSummaryWithData,
  collectPortfolioStatesFromMeta,
  formatNetworkPortfolioSelectorDetail,
  formatNetworkPortfolioSubtitle,
  type NetworkSummaryData,
  type NetworkRiskFlagsData,
  type NetworkDashboardMeta,
  type NetworkDashboardRequestOptions,
  type NetworkExecutiveSummaryData,
  type NetworkDashboardView,
} from "@services/networkDashboard";
import {
  getCurrentCalendarQuarter,
  formatReportingPeriodLabel,
  reportingPeriodEndDateIso,
  type NetworkReportingPeriod,
} from "@utils/networkReportingPeriod";
import { DASHBOARD } from "./dashboard/dashboardTheme";
import NetworkHeaderStrip from "./network/NetworkHeaderStrip";
import NetworkReportingPeriodSelector from "./network/NetworkReportingPeriodSelector";
import { filterDashboardDataQualityWarnings } from "@utils/networkCmsSnapshotWarnings";
import { exportNetworkRiskFlagsToExcel } from "@utils/networkRiskFlagsExport";
import {
  ExecutiveRiskFlagsPanel,
  RatingsPanel,
  SurveyPanel,
  StaffingPanel,
  QualityPanel,
  VbpPanel,
} from "./network/NetworkPanels";
import NetworkDeleteDialog from "./network/NetworkDeleteDialog";
import {
  getSelectedFacilityFromSession,
  notifySelectedFacilitySessionUpdated,
  SELECTED_FACILITY_STORAGE_KEY,
} from "@utils/facilityUtils";
import {
  invalidateNetworksCache,
  listNetworks,
  type NetworkRecord,
} from "@services/networkManagement";
import { useFacilityLayoutDataOptional } from "@context";
import NetworkDashboardToggle from "./network/NetworkDashboardToggle";
import NetworkExecutiveSummary from "./network/NetworkExecutiveSummary";
import NetworkDefineModal from "./network/NetworkDefineModal";
import FacilityNetworkFqhcDashboardView from "./facilityNetwork/FacilityNetworkFqhcDashboardView";

export type NetworkSubTabId =
  | "flags"
  | "ratings"
  | "survey"
  | "staffing"
  | "quality"
  | "vbp"
  | "fqhc";

const SUB_TABS: { id: NetworkSubTabId; label: string; icon: React.ReactNode }[] = [
  { id: "flags", label: "Executive risk flags", icon: <FlagOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "ratings", label: "Ratings & benchmark deltas", icon: <StarOutlineIcon sx={{ fontSize: 16 }} /> },
  { id: "survey", label: "Survey & deficiency drivers", icon: <WarningAmberOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "staffing", label: "Staffing & workforce stability", icon: <GroupsOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "quality", label: "Clinical quality measures", icon: <FavoriteBorderOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "vbp", label: "SNF VBP & QRP financial performance", icon: <AttachMoneyOutlinedIcon sx={{ fontSize: 16 }} /> },
  { id: "fqhc", label: "FQHC public data", icon: <LocalHospitalOutlinedIcon sx={{ fontSize: 16 }} /> },
];

const EMPTY_NETWORK_ERROR =
  "Network dashboard is available only for a network with linked facilities.";

export interface NetworkTabProps {
  facility: MyFacilityListRow;
  isTabActive: boolean;
}

export default function NetworkTab({ facility, isTabActive }: NetworkTabProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const routeState = location.state as {
    networkSubTab?: NetworkSubTabId;
    networkDashboardView?: NetworkDashboardView;
  } | null;
  const facilityId = facility.facility_id;
  const networkId = facility.is_network ? facility.network_id : undefined;
  const portfolioKey = networkId ?? facilityId;
  const [subTab, setSubTab] = useState<NetworkSubTabId>(routeState?.networkSubTab ?? "flags");
  const [summary, setSummary] = useState<NetworkSummaryData | null>(null);
  const [meta, setMeta] = useState<NetworkDashboardMeta | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryHasData, setSummaryHasData] = useState(false);
  const [panelLoading, setPanelLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [riskFlags, setRiskFlags] = useState<NetworkRiskFlagsData | null>(null);
  const [ratings, setRatings] = useState<Record<string, unknown> | null>(null);
  const [survey, setSurvey] = useState<Record<string, unknown> | null>(null);
  const [staffing, setStaffing] = useState<Record<string, unknown> | null>(null);
  const [quality, setQuality] = useState<Record<string, unknown> | null>(null);
  const [vbp, setVbp] = useState<Record<string, unknown> | null>(null);
  const [riskFlagFilter, setRiskFlagFilter] = useState<string | null>(null);
  const [riskFlagsExportLoading, setRiskFlagsExportLoading] = useState(false);
  const loadedPanelsRef = useRef<Set<NetworkSubTabId>>(new Set());
  const prevRiskFlagFilterRef = useRef<string | null | undefined>(undefined);
  const summaryRequestIdRef = useRef(0);
  const [reportingPeriod, setReportingPeriod] = useState<NetworkReportingPeriod>(() =>
    getCurrentCalendarQuarter(),
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteMessage, setDeleteMessage] = useState<string | null>(null);
  const [dashboardView, setDashboardView] = useState<NetworkDashboardView>(
    routeState?.networkDashboardView ?? "operational",
  );
  const [executiveSummary, setExecutiveSummary] = useState<NetworkExecutiveSummaryData | null>(null);
  const [executiveLoading, setExecutiveLoading] = useState(false);
  const [defineModalOpen, setDefineModalOpen] = useState(false);
  const [listedNetworkForDelete, setListedNetworkForDelete] = useState<NetworkRecord | null>(null);
  const layoutData = useFacilityLayoutDataOptional();

  const requestOptions = useMemo<NetworkDashboardRequestOptions>(
    () => ({ reportingPeriod }),
    [reportingPeriod],
  );

  // Prefer listNetworks / layout NetworkRecord name for the delete confirm (full stamped name + id).
  useEffect(() => {
    if (networkId == null) {
      setListedNetworkForDelete(null);
      return;
    }
    const fromLayout = layoutData?.networks?.find((row) => row.id === networkId) ?? null;
    if (fromLayout) {
      setListedNetworkForDelete(fromLayout);
      return;
    }
    let cancelled = false;
    Promise.resolve()
      .then(() => listNetworks())
      .then((rows) => {
        if (!cancelled) {
          setListedNetworkForDelete(rows.find((row) => row.id === networkId) ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setListedNetworkForDelete(null);
      });
    return () => {
      cancelled = true;
    };
  }, [networkId, layoutData?.networks]);

  const loadSummary = useCallback(async () => {
    const requestId = ++summaryRequestIdRef.current;
    setSummaryLoading(true);
    setSummaryHasData(false);
    setError(null);
    try {
      const result = await findNetworkSummaryWithData(
        (options) =>
          networkId
            ? getNetworkSummaryForNetwork(networkId, options)
            : getNetworkSummary(facilityId, options),
        reportingPeriod,
      );
      if (summaryRequestIdRef.current !== requestId) return;

      setSummary(result.response?.data ?? null);
      setMeta(result.response?.meta ?? null);
      setSummaryHasData(result.hasData);
      if (result.hasData && result.reportingPeriod) {
        if (
          result.reportingPeriod.quarter !== reportingPeriod.quarter ||
          result.reportingPeriod.year !== reportingPeriod.year
        ) {
          setReportingPeriod(result.reportingPeriod);
        }
      } else if (networkId != null) {
        setDeleteDialogOpen(true);
      }
    } catch (e) {
      if (summaryRequestIdRef.current !== requestId) return;
      const message = e instanceof Error ? e.message : "Failed to load network summary";
      if (networkId != null && message === EMPTY_NETWORK_ERROR) {
        setError(null);
        setDeleteDialogOpen(true);
      } else {
        setError(message);
      }
      setSummary(null);
      setSummaryHasData(false);
    } finally {
      if (summaryRequestIdRef.current === requestId) {
        setSummaryLoading(false);
      }
    }
  }, [facilityId, networkId, reportingPeriod]);

  const loadExecutiveSummary = useCallback(async () => {
    setExecutiveLoading(true);
    setError(null);
    try {
      const res = networkId
        ? await getNetworkExecutiveSummaryForNetwork(networkId, requestOptions)
        : await getNetworkExecutiveSummary(facilityId, requestOptions);
      setExecutiveSummary(res.data);
      setMeta((prev) => ({ ...prev, ...res.meta }));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load executive summary");
      setExecutiveSummary(null);
    } finally {
      setExecutiveLoading(false);
    }
  }, [facilityId, networkId, requestOptions]);

  const handleDashboardViewChange = useCallback(
    (view: NetworkDashboardView) => {
      setDashboardView(view);
      navigate("/dashboard/facilities", {
        replace: true,
        state: {
          ...(location.state as Record<string, unknown>),
          currentView: "network",
          ...(networkId != null ? { networkId } : {}),
          networkSubTab: subTab,
          networkDashboardView: view,
        },
      });
    },
    [navigate, location.state, networkId, subTab],
  );

  const facilityLocations = meta?.ccn_facility_locations;
  const facilityNameToCcn = useMemo(() => {
    const map: Record<string, string> = {};
    const names = meta?.ccn_facility_names;
    if (!names) return map;
    for (const [ccn, name] of Object.entries(names)) {
      const key = (name || "").trim().toLowerCase();
      if (key) map[key] = ccn;
    }
    return map;
  }, [meta?.ccn_facility_names]);

  const portfolioFacilityCount =
    summary?.portfolio_ccn_count ??
    summary?.facility_count ??
    meta?.facility_count ??
    meta?.ccns?.length ??
    0;

  const subTabCounts = useMemo<Partial<Record<NetworkSubTabId, string>>>(() => {
    const counts: Partial<Record<NetworkSubTabId, string>> = {};
    if (riskFlags?.total_count != null && riskFlags.total_count > 0) {
      counts.flags = String(riskFlags.total_count);
    }
    const surveyProfile = (survey?.citation_profile as Record<string, unknown> | undefined) ?? {};
    const surveyTotal = Number(surveyProfile.total_health_deficiencies ?? 0);
    if (surveyTotal > 0) {
      counts.survey = String(surveyTotal);
    }
    return counts;
  }, [riskFlags?.total_count, survey?.citation_profile]);

  const loadPanel = useCallback(
    async (panel: NetworkSubTabId, options?: { force?: boolean }) => {
      if (!options?.force && loadedPanelsRef.current.has(panel)) return;
      setPanelLoading(true);
      try {
        switch (panel) {
          case "flags": {
            const flagsRequest: NetworkDashboardRequestOptions = {
              ...requestOptions,
              limit: Math.max(portfolioFacilityCount, 1),
              ...(riskFlagFilter ? { flag: riskFlagFilter } : {}),
            };
            const res = networkId
              ? await getNetworkRiskFlagsForNetwork(networkId, flagsRequest)
              : await getNetworkRiskFlags(facilityId, flagsRequest);
            let flagsData = res.data;
            if (
              flagsData.total_count > flagsData.facilities.length &&
              flagsData.total_count <= 25000
            ) {
              const fullRes = networkId
                ? await getNetworkRiskFlagsForNetwork(networkId, {
                    ...flagsRequest,
                    limit: flagsData.total_count,
                  })
                : await getNetworkRiskFlags(facilityId, {
                    ...flagsRequest,
                    limit: flagsData.total_count,
                  });
              flagsData = fullRes.data;
              setMeta((prev) => ({ ...prev, ...fullRes.meta }));
            } else {
              setMeta((prev) => ({ ...prev, ...res.meta }));
            }
            setRiskFlags(flagsData);
            break;
          }
          case "ratings": {
            const res = networkId
              ? await getNetworkRatingsForNetwork(networkId, requestOptions)
              : await getNetworkRatings(facilityId, requestOptions);
            setRatings(res.data);
            setMeta((prev) => ({ ...prev, ...res.meta }));
            break;
          }
          case "survey": {
            const res = networkId
              ? await getNetworkSurveyDriversForNetwork(networkId, requestOptions)
              : await getNetworkSurveyDrivers(facilityId, requestOptions);
            setSurvey(res.data);
            setMeta((prev) => ({ ...prev, ...res.meta }));
            break;
          }
          case "staffing": {
            const res = networkId
              ? await getNetworkStaffingForNetwork(networkId, requestOptions)
              : await getNetworkStaffing(facilityId, requestOptions);
            setStaffing(res.data);
            setMeta((prev) => ({ ...prev, ...res.meta }));
            break;
          }
          case "quality": {
            const res = networkId
              ? await getNetworkQualityMeasuresForNetwork(networkId, requestOptions)
              : await getNetworkQualityMeasures(facilityId, requestOptions);
            setQuality(res.data);
            setMeta((prev) => ({ ...prev, ...res.meta }));
            break;
          }
          case "vbp": {
            const res = networkId
              ? await getNetworkVbpForNetwork(networkId, requestOptions)
              : await getNetworkVbp(facilityId, requestOptions);
            setVbp(res.data);
            setMeta((prev) => ({ ...prev, ...res.meta }));
            break;
          }
        }
      loadedPanelsRef.current.add(panel);
    } catch (e) {
      setError(e instanceof Error ? e.message : `Failed to load ${panel}`);
    } finally {
      setPanelLoading(false);
    }
  },
    [facilityId, networkId, requestOptions, portfolioFacilityCount, riskFlagFilter],
  );

  // Reset panel/dialog state only when the selected portfolio changes — not when
  // reporting period refreshes mid-delete (that was closing the confirm modal).
  useEffect(() => {
    if (!isTabActive) return;
    loadedPanelsRef.current = new Set();
    prevRiskFlagFilterRef.current = undefined;
    setRiskFlagFilter(null);
    setRiskFlags(null);
    setRatings(null);
    setSurvey(null);
    setStaffing(null);
    setQuality(null);
    setVbp(null);
    setDeleteDialogOpen(false);
  }, [isTabActive, portfolioKey]);

  useEffect(() => {
    if (!isTabActive) return;
    void loadSummary();
    return () => {
      summaryRequestIdRef.current += 1;
    };
  }, [isTabActive, portfolioKey, reportingPeriod, loadSummary]);

  useEffect(() => {
    if (!isTabActive || dashboardView !== "executive" || !summaryHasData) return;
    void loadExecutiveSummary();
  }, [
    isTabActive,
    dashboardView,
    portfolioKey,
    reportingPeriod,
    summaryHasData,
    loadExecutiveSummary,
  ]);

  useEffect(() => {
    if (!isTabActive || dashboardView !== "operational" || !summaryHasData) return;
    if (subTab === "fqhc") return;
    void loadPanel(subTab);
  }, [isTabActive, dashboardView, subTab, loadPanel, summaryHasData]);

  useEffect(() => {
    if (!isTabActive || subTab !== "flags") return;
    if (prevRiskFlagFilterRef.current === undefined) {
      prevRiskFlagFilterRef.current = riskFlagFilter;
      return;
    }
    if (prevRiskFlagFilterRef.current === riskFlagFilter) return;
    prevRiskFlagFilterRef.current = riskFlagFilter;
    loadedPanelsRef.current.delete("flags");
    void loadPanel("flags", { force: true });
  }, [riskFlagFilter, isTabActive, subTab, loadPanel]);

  const portfolioSubtitle = formatNetworkPortfolioSubtitle(
    portfolioFacilityCount,
    reportingPeriodEndDateIso(reportingPeriod.quarter, reportingPeriod.year),
  );
  const selectedNetworkName = (
    summary?.chain_name ||
    meta?.root_facility_name ||
    facility.org_name ||
    facility.facility_name ||
    "Selected network"
  ).toUpperCase();
  const portfolioStates =
    summary?.portfolio_states?.length
      ? summary.portfolio_states
      : collectPortfolioStatesFromMeta(meta);
  const selectedNetworkDetail = formatNetworkPortfolioSelectorDetail({
    facilityCount: portfolioFacilityCount || summary?.facility_count || 0,
    states: portfolioStates,
    chainId: summary?.chain_id,
    networkId,
  });

  const dashboardWarnings = filterDashboardDataQualityWarnings(meta?.data_quality_warnings);

  const networkForDelete = useMemo<NetworkRecord | null>(() => {
    if (networkId == null) return null;
    if (listedNetworkForDelete && listedNetworkForDelete.id === networkId) {
      return listedNetworkForDelete;
    }
    return {
      id: networkId,
      name: facility.facility_name || selectedNetworkName,
      network_group_id: facility.facility_group_id ?? "",
      country_code: facility.country_code || "USA",
      vertical: facility.vertical || "healthcare",
      is_active: true,
      child_facility_count: portfolioFacilityCount,
      linked_ccn_count: portfolioFacilityCount,
    };
  }, [
    networkId,
    listedNetworkForDelete,
    facility.facility_name,
    facility.facility_group_id,
    facility.country_code,
    facility.vertical,
    selectedNetworkName,
    portfolioFacilityCount,
  ]);

  const handleFacilitySelect = useCallback(
    (ccn: string) => {
      navigate("/dashboard/facilities", {
        state: {
          currentView: "network-facility",
          ccn,
          ...(networkId != null ? { networkId } : { facilityId }),
          networkSubTab: subTab,
          networkDashboardView: dashboardView,
          networkName: selectedNetworkName,
        },
      });
    },
    [navigate, networkId, facilityId, subTab, dashboardView, selectedNetworkName],
  );

  const handleExportRiskFlags = useCallback(async () => {
    if (!riskFlags || riskFlagsExportLoading) return;
    setRiskFlagsExportLoading(true);
    setError(null);
    try {
      let exportData = riskFlags;
      if (exportData.facilities.length < exportData.total_count) {
        const flagsRequest: NetworkDashboardRequestOptions = {
          ...requestOptions,
          limit: exportData.total_count,
          ...(riskFlagFilter ? { flag: riskFlagFilter } : {}),
        };
        const res = networkId
          ? await getNetworkRiskFlagsForNetwork(networkId, flagsRequest)
          : await getNetworkRiskFlags(facilityId, flagsRequest);
        exportData = res.data;
      }
      exportNetworkRiskFlagsToExcel(exportData, {
        facilityLocations,
        facilityNameToCcn,
        networkName: selectedNetworkName,
        flagFilter: riskFlagFilter,
        reportingPeriodLabel: formatReportingPeriodLabel(reportingPeriod),
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to export risk flags");
    } finally {
      setRiskFlagsExportLoading(false);
    }
  }, [
    riskFlags,
    riskFlagsExportLoading,
    requestOptions,
    riskFlagFilter,
    networkId,
    facilityId,
    facilityLocations,
    facilityNameToCcn,
    selectedNetworkName,
    reportingPeriod,
  ]);

  return (
    <Box
      sx={{
        fontFamily: "Rethink Sans, sans-serif",
        width: "100%",
        boxSizing: "border-box",
        py: { xs: 1.25, sm: 1.5 },
        px: 0,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(0, 1fr) minmax(320px, 380px)" },
          gap: 2,
          mb: dashboardView === "operational" ? 2.5 : 2,
          alignItems: "start",
        }}
      >
        <Box
          sx={{
            bgcolor: "#fff",
            border: `1px solid ${DASHBOARD.border}`,
            borderRadius: "12px",
            minHeight: 58,
            px: 2,
            py: 1.25,
            display: "grid",
            gridTemplateColumns: "120px 28px minmax(0, 1fr) 20px",
            alignItems: "center",
            gap: 1.25,
          }}
        >
          <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted }}>
            Selected Network
          </Typography>
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: "7px",
              bgcolor: DASHBOARD.iconButtonBg,
              color: DASHBOARD.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 15 }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 700, color: DASHBOARD.header, lineHeight: 1.2 }}>
              {selectedNetworkName}
            </Typography>
            <Typography sx={{ fontSize: "12px", color: DASHBOARD.text, mt: 0.4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {selectedNetworkDetail || portfolioSubtitle}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon sx={{ fontSize: 16, color: DASHBOARD.textMuted }} />
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, minWidth: 0 }}>
          <Box
            sx={{
              bgcolor: "#fff",
              border: `1px solid ${DASHBOARD.border}`,
              borderRadius: "12px",
              minHeight: 58,
              px: 2,
              py: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
              width: "100%",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(72px, auto) 1fr",
                alignItems: "center",
                gap: 1.5,
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography sx={{ fontSize: "12px", color: DASHBOARD.textMuted, whiteSpace: "nowrap" }}>
                Reporting Period
              </Typography>
              <NetworkReportingPeriodSelector
                period={reportingPeriod}
                onChange={setReportingPeriod}
              />
            </Box>
            {networkId != null ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, flexShrink: 0 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setDefineModalOpen(true)}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Add facilities
                </Button>
                <Button
                  color="error"
                  variant="outlined"
                  size="small"
                  onClick={() => setDeleteDialogOpen(true)}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  Delete network
                </Button>
              </Box>
            ) : null}
          </Box>
          <NetworkDashboardToggle value={dashboardView} onChange={handleDashboardViewChange} />
        </Box>
      </Box>

      {dashboardView === "operational" ? (
      <Box sx={{ mb: 1.25, display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
        <GroupsOutlinedIcon sx={{ fontSize: 17, color: DASHBOARD.accent }} />
        <Typography
          component="h2"
          sx={{
            fontSize: "22px",
            fontWeight: 700,
            color: DASHBOARD.header,
            letterSpacing: "-0.01em",
            m: 0,
          }}
        >
          Network
        </Typography>
        <Typography
          sx={{
            fontSize: "13px",
            color: DASHBOARD.textMuted,
            whiteSpace: { xs: "normal", md: "nowrap" },
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {portfolioSubtitle}
        </Typography>
      </Box>
      ) : null}

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {deleteMessage ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setDeleteMessage(null)}>
          {deleteMessage}
        </Alert>
      ) : null}

      {dashboardWarnings.map((w) => (
        <Alert key={w} severity="info" sx={{ mb: 1 }}>
          {w}
        </Alert>
      ))}

      {dashboardView === "operational" ? (
        <NetworkHeaderStrip summary={summary} loading={summaryLoading} />
      ) : null}

      {dashboardView === "executive" ? (
        <NetworkExecutiveSummary
          data={executiveSummary}
          meta={meta}
          loading={executiveLoading}
          networkLabel={selectedNetworkDetail || portfolioSubtitle}
          onDefineNetwork={() => setDefineModalOpen(true)}
          onFacilitySelect={handleFacilitySelect}
        />
      ) : (
        <>
      <Box
        sx={{
          display: "flex",
          flexWrap: "nowrap",
          overflowX: "auto",
          bgcolor: "#fff",
          border: `1px solid ${DASHBOARD.border}`,
          borderBottom: "1px solid #fff",
          borderRadius: "12px 12px 0 0",
          mb: "-1px",
          position: "relative",
          zIndex: 2,
          "& .MuiButton-root": {
            textTransform: "none",
            fontSize: "13px",
            fontWeight: 600,
            borderRadius: 0,
            color: DASHBOARD.text,
            px: 2,
            py: 1.35,
            minHeight: 43,
            whiteSpace: "nowrap",
            borderRight: `1px solid ${DASHBOARD.borderSubtle}`,
            justifyContent: "flex-start",
            flexShrink: 0,
            lineHeight: 1.2,
          },
        }}
      >
        {SUB_TABS.filter((t) => t.id !== "fqhc" || networkId != null).map((t) => (
          <Button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            sx={{
              bgcolor: subTab === t.id ? "#fff" : "#f6fbfc",
              color: subTab === t.id ? `${DASHBOARD.header} !important` : undefined,
              fontWeight: subTab === t.id ? "700 !important" : undefined,
              borderBottom: subTab === t.id ? "1px solid #fff" : `1px solid ${DASHBOARD.borderSubtle}`,
              "&:first-of-type": { borderTopLeftRadius: "12px" },
              "&:last-of-type": { borderTopRightRadius: "12px", borderRight: 0 },
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            {t.icon}
            <Box component="span" sx={{ ml: 1, display: { xs: "none", md: "inline" } }}>
              {t.label}
            </Box>
            <Box component="span" sx={{ ml: 1, display: { xs: "inline", md: "none" } }}>
              {t.id === "flags" ? "Risk" : t.id === "ratings" ? "Ratings" : t.id === "survey" ? "Survey" : t.id === "staffing" ? "Staff" : t.id === "quality" ? "QM" : t.id === "fqhc" ? "FQHC" : "VBP"}
            </Box>
            {subTabCounts[t.id] ? (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.8,
                  py: 0.1,
                  borderRadius: 10,
                  fontSize: "11px",
                  fontWeight: 700,
                  lineHeight: 1.4,
                  bgcolor: subTab === t.id ? DASHBOARD.accent : DASHBOARD.iconButtonBg,
                  color: subTab === t.id ? "#fff" : DASHBOARD.accent,
                }}
              >
                {subTabCounts[t.id]}
              </Box>
            ) : null}
          </Button>
        ))}
      </Box>

      {subTab === "flags" && (
        <ExecutiveRiskFlagsPanel
          data={riskFlags}
          loading={panelLoading && !riskFlags}
          facilityLocations={facilityLocations}
          facilityNameToCcn={facilityNameToCcn}
          activeFlagFilter={riskFlagFilter}
          onFlagFilterChange={setRiskFlagFilter}
          onFacilitySelect={handleFacilitySelect}
          onExport={() => void handleExportRiskFlags()}
          exportLoading={riskFlagsExportLoading}
        />
      )}
      {subTab === "ratings" && (
        <RatingsPanel
          data={ratings}
          loading={panelLoading && !ratings}
          facilityLocations={facilityLocations}
          facilityNameToCcn={facilityNameToCcn}
        />
      )}
      {subTab === "survey" && (
        <SurveyPanel
          data={survey}
          loading={panelLoading && !survey}
          facilityLocations={facilityLocations}
          facilityNameToCcn={facilityNameToCcn}
        />
      )}
      {subTab === "staffing" && (
        <StaffingPanel
          data={staffing}
          loading={panelLoading && !staffing}
          facilityLocations={facilityLocations}
          facilityNameToCcn={facilityNameToCcn}
        />
      )}
      {subTab === "quality" && (
        <QualityPanel
          data={quality}
          loading={panelLoading && !quality}
          facilityLocations={facilityLocations}
          facilityNameToCcn={facilityNameToCcn}
        />
      )}
      {subTab === "vbp" && (
        <VbpPanel
          data={vbp}
          loading={panelLoading && !vbp}
          facilityLocations={facilityLocations}
          facilityNameToCcn={facilityNameToCcn}
        />
      )}
      {subTab === "fqhc" && networkId != null && (
        <FacilityNetworkFqhcDashboardView
          members={[]}
          selectedFacilityId={null}
          networkId={networkId}
          onBack={() => setSubTab("flags")}
          onOpenNetworkFacility={handleFacilitySelect}
        />
      )}
        </>
      )}

      <NetworkDefineModal
        open={defineModalOpen}
        networkId={networkId ?? null}
        onClose={() => setDefineModalOpen(false)}
        onImported={() => {
          window.dispatchEvent(new CustomEvent("unicomply:networks-changed"));
          void loadSummary();
          void loadExecutiveSummary();
          handleDashboardViewChange("executive");
        }}
      />

      <NetworkDeleteDialog
        open={deleteDialogOpen}
        network={networkForDelete}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={(result) => {
          invalidateNetworksCache();
          // Drop session selection if it pointed at this holding-company network.
          try {
            const selected = getSelectedFacilityFromSession<{
              is_network?: boolean;
              network_id?: number;
              facility_id?: number;
            }>();
            if (
              selected?.is_network &&
              selected.network_id != null &&
              Number(selected.network_id) === Number(result.network_id)
            ) {
              sessionStorage.removeItem(SELECTED_FACILITY_STORAGE_KEY);
              notifySelectedFacilitySessionUpdated();
            }
          } catch {
            /* ignore */
          }
          window.dispatchEvent(new CustomEvent("unicomply:networks-changed"));
          // Empty/default Network Intelligence state (no selected network).
          navigate("/dashboard/network/intel", {
            replace: true,
            state: { currentView: "network-intelligence" },
          });
          setDeleteMessage(
            `Deleted network "${result.network_name}". Unlinked ${result.unlinked_count} facilities; removed ${result.deleted_orphan_count} import-only facilities.`,
          );
        }}
      />
    </Box>
  );
}
